/**
 * HeaderEssentialActions Component
 *
 * Always-visible action icons in the header bar.
 * Provides quick access to player stats, live scores, roster intel, and notifications at all widths.
 * Also owns global keyboard shortcuts since this component is always mounted.
 */

import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { Search, SlidersHorizontal, UsersRound } from 'lucide-react';
import { type ReactElement, useEffect, useMemo } from 'react';
import { NotificationBell } from './NotificationBell';

const isMac =
  typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
const modKey = isMac ? '⌘' : 'Ctrl+';

export function HeaderEssentialActions(): ReactElement {
  const {
    openQuickStats,
    openConfigPanel,
    openRosterIntel,
    closeAllModals,
    isQuickStatsOpen,
    isConfigPanelOpen,
  } = useGlobalShortcutsStore();

  // Derived: roster intel is open when gameModal is open with lineup tab
  const isGameModalOpen = useGlobalShortcutsStore((s) => s.gameModal !== null);

  const searchTooltip = useMemo(() => `Search players/teams (${modKey}K)`, []);

  // Global keyboard shortcuts — owned here because this component is always mounted
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        if (e.key !== 'Escape') return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openQuickStats();
      }

      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        openConfigPanel();
      }

      if (e.key === 'Escape') {
        if (isQuickStatsOpen || isConfigPanelOpen || isGameModalOpen) {
          e.preventDefault();
          closeAllModals();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    openQuickStats,
    openConfigPanel,
    closeAllModals,
    isQuickStatsOpen,
    isConfigPanelOpen,
    isGameModalOpen,
  ]);

  const btnBase =
    'rounded-lg bg-theme-surface transition flex items-center justify-center hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-text p-2 min-h-10 min-w-10';
  const btnActive =
    'rounded-lg bg-theme-surface-hover transition flex items-center justify-center text-theme-text p-2 min-h-10 min-w-10';

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      {/* Player Stats Lookup */}
      <button
        type="button"
        onClick={openQuickStats}
        className={isQuickStatsOpen ? btnActive : btnBase}
        title={searchTooltip}
        aria-label="Search players and teams"
      >
        <Search className="w-5 h-5" />
      </button>

      {/* Roster Intel / Game Modal */}
      <button
        type="button"
        onClick={openRosterIntel}
        className={isGameModalOpen ? btnActive : btnBase}
        title="Roster Intel"
        aria-label="Roster Intel"
      >
        <UsersRound className="w-5 h-5" />
      </button>

      {/* Notifications */}
      <NotificationBell />

      {/* AI Config */}
      <button
        data-testid="header-config-button"
        type="button"
        onClick={openConfigPanel}
        className={isConfigPanelOpen ? btnActive : btnBase}
        title="AI Config Settings"
        aria-label="AI analysis settings"
      >
        <SlidersHorizontal className="w-5 h-5" />
      </button>
    </div>
  );
}
